import { createContext, type ReactNode, useContext } from 'react';

export type FlexiblePropertiesSearchContextValue = {
  searchText: string;
  /**
   * Listing responses don't include a per-listing isArchived flag, so we track it ourselves via
   * the requested view and treat it as each listing's archived status (e.g. passed as
   * isArchiveView to properties/options).
   */
  includeArchived: boolean;
};

const defaultContextValue: FlexiblePropertiesSearchContextValue = {
  searchText: '',
  includeArchived: false,
};

/**
 * Holds the committed (debounced) search text and archived filter for the FlexiblePropertiesPage so any
 * table under the page (units now; payments, leases, inquiries next) can read them without prop drilling.
 * The page owns the values via its filters state and passes them to the provider.
 */
const FlexiblePropertiesSearchContext = createContext<FlexiblePropertiesSearchContextValue>(defaultContextValue);

type FlexiblePropertiesSearchProviderProps = FlexiblePropertiesSearchContextValue & {
  children: ReactNode;
};

export const FlexiblePropertiesSearchProvider = ({
  searchText,
  includeArchived,
  children,
}: FlexiblePropertiesSearchProviderProps) => (
  <FlexiblePropertiesSearchContext.Provider value={{ searchText, includeArchived }}>
    {children}
  </FlexiblePropertiesSearchContext.Provider>
);

export const useFlexiblePropertiesSearch = () => useContext(FlexiblePropertiesSearchContext);
